export default {
  // Hero
  'hero.badge': 'Transparent Process',
  'hero.title': 'How It Works',
  'hero.subtitle': 'From Product Selection to Final Delivery',
  'hero.imageAlt': 'Cosmetic packaging factory production line',

  // Step labels
  'step.label': 'Step',
  'step.titleSuffix': 'Cosmetic Packaging Process',
  'hero.desc':
    'From the moment you share your idea to the safe delivery of the finished product, see how every stage of your manufacturing project is managed transparently and professionally.',

  // Process overview
  'overview.label': 'Process Overview',
  'overview.title': 'The Entire Journey at a Glance',
  'overview.subtitle':
    'Thirteen carefully managed stages take your idea from a simple inquiry to a delivered product. Each stage is connected and quality-controlled so nothing is left to chance.',
  'overview.track1.title': 'Discovery & Planning',
  'overview.track1.desc':
    'From your first inquiry through specification, quotation, sampling and approval — steps 1 to 7.',
  'overview.track2.title': 'Production & Delivery',
  'overview.track2.desc':
    'From mass production through inspection, packaging, shipping and after-sales — steps 8 to 13.',

  // Step 1
  'steps.1.title': 'Product Search & Inquiry',
  'steps.1.desc':
    'Find the right container by browsing our product catalog, category search, and image-based similar search, then reach out with your requirements.',
  'steps.1.note':
    'Sending a product image together with your target quantity helps us review your inquiry faster and more accurately.',
  'steps.1.item1': 'Product search and comparison',
  'steps.1.item2': 'Find similar products by image',
  'steps.1.item3': 'Check MOQ, material, capacity and stock status',
  'steps.1.item4': 'Custom product inquiry if no matching item is found',
  'steps.1.item5': 'Share product, quantity, country and expected timeline',

  // Step 2
  'steps.2.title': 'Requirements & Specification Review',
  'steps.2.desc':
    'We confirm your requirements and product specifications in detail to make sure every detail is understood before quoting.',
  'steps.2.note':
    'Accurate specification review determines the quality of both your quotation and your samples.',
  'steps.2.item1': 'Container type and capacity',
  'steps.2.item2': 'Material and color',
  'steps.2.item3': 'Pump, cap and accessory configuration',
  'steps.2.item4': 'Contents-to-container compatibility review',
  'steps.2.item5': 'Printing, coating, labeling and finishing options',
  'steps.2.item6': 'Target quantity and delivery date',
  'steps.2.item7': 'Shipping country and trade terms',
  'steps.2.item8': 'Customer design file review',

  // Step 3
  'steps.3.title': 'Quotation & Project Confirmation',
  'steps.3.desc':
    'Based on the confirmed specifications, we review the quotation and production terms together with you.',
  'steps.3.note':
    'Quotations may vary depending on final specifications, quantity, finishing, exchange rates and shipping terms.',
  'steps.3.item1': 'Product unit price',
  'steps.3.item2': 'MOQ',
  'steps.3.item3': 'Sample and mold costs',
  'steps.3.item4': 'Printing and finishing costs',
  'steps.3.item5': 'Packaging and estimated shipping costs',
  'steps.3.item6': 'Estimated production period',
  'steps.3.item7': 'Payment terms and quotation validity',
  'steps.3.item8': 'International trade terms confirmation',

  // Step 4
  'steps.4.title': 'Sample Development & Approval',
  'steps.4.desc':
    'Before mass production, we verify the product and design through physical samples and prototypes.',
  'steps.4.note':
    'We do not proceed with significant custom production without sample approval — this is our core quality principle.',
  'steps.4.item1': 'Existing product sample dispatch',
  'steps.4.item2': 'Custom color and finishing sample making',
  'steps.4.item3': 'Design proof review',
  'steps.4.item4': 'Mold or prototype making',
  'steps.4.item5': 'Customer sample evaluation',
  'steps.4.item6': 'Revision feedback incorporation',
  'steps.4.item7': 'Final sample approval',

  // Step 5
  'steps.5.title': 'Order & Payment Confirmation',
  'steps.5.desc':
    'Once the final specification and sample are approved, we confirm the formal order.',
  'steps.5.note':
    'Production begins only after the Proforma Invoice and contract terms are confirmed.',
  'steps.5.item1': 'Final quotation or Proforma Invoice confirmation',
  'steps.5.item2': 'Product specification sheet confirmation',
  'steps.5.item3': 'Order quantity confirmation',
  'steps.5.item4': 'Delivery schedule confirmation',
  'steps.5.item5': 'Shipping information confirmation',
  'steps.5.item6': 'Contract and payment terms confirmation',
  'steps.5.item7': 'Production order number creation',

  // Step 6
  'steps.6.title': 'Pre-Production Preparation',
  'steps.6.desc':
    'Before mass production begins, we re-check every production condition one more time.',
  'steps.6.note':
    'Re-verifying all conditions before production minimizes mass-production risk.',
  'steps.6.item1': 'Final approved sample vs production spec comparison',
  'steps.6.item2': 'Raw material and accessory preparation',
  'steps.6.item3': 'Mold and production equipment inspection',
  'steps.6.item4': 'Color and printing data confirmation',
  'steps.6.item5': 'Production plan and quality inspection standard setup',
  'steps.6.item6': 'Pre-production internal approval',

  // Step 7
  'steps.7.title': 'Raw Material & Incoming Quality Control',
  'steps.7.desc':
    'We inspect the raw materials and accessories entering production before they are used.',
  'steps.7.note':
    'Materials that fail to meet the standard are never introduced into production.',
  'steps.7.item1': 'Raw material verification',
  'steps.7.item2': 'Pump, cap and accessory inspection',
  'steps.7.item3': 'Color and material confirmation',
  'steps.7.item4': 'Supplied accessory specification check',
  'steps.7.item5': 'Separation of defective or substandard materials',
  'steps.7.item6': 'Inspection result recording',

  // Step 8
  'steps.8.title': 'Mass Production',
  'steps.8.desc':
    'Your product is manufactured at our automated large-scale production facilities.',
  'steps.8.note':
    'Batch-level traceability keeps the full production history of every order manageable.',
  'steps.8.item1': 'Automated production lines',
  'steps.8.item2': 'Injection, blow, tube and glass container production',
  'steps.8.item3': 'Process-by-process production management',
  'steps.8.item4': 'Working condition and production quantity control',
  'steps.8.item5': 'Production batch and traceability management',
  'steps.8.item6': 'Supervision by skilled workers and quality controllers',

  // Step 9
  'steps.9.title': 'In-Process Quality Control',
  'steps.9.desc':
    'In-line inspection catches potential problems early during production.',
  'steps.9.note':
    'Early problem detection lowers the defect rate across the entire batch.',
  'steps.9.item1': 'Appearance and color check',
  'steps.9.item2': 'Size and weight check',
  'steps.9.item3': 'Assembly condition inspection',
  'steps.9.item4': 'Pump operation inspection',
  'steps.9.item5': 'Leakage and sealing check',
  'steps.9.item6': 'Printing position and quality check',
  'steps.9.item7': 'Separation of in-process defective items',
  'steps.9.item8': 'Production condition adjustment when needed',

  // Step 10
  'steps.10.title': 'Final Quality Inspection',
  'steps.10.desc':
    'Completed products are inspected one final time before shipment.',
  'steps.10.note':
    'Certifications, inspection standards and pass rates are applied only as actually verified — specific values are shown as placeholders until confirmed.',
  'steps.10.item1': 'Final appearance inspection',
  'steps.10.item2': 'Quantity inspection',
  'steps.10.item3': 'Dimension and specification inspection',
  'steps.10.item4': 'Function and assembly inspection',
  'steps.10.item5': 'Leakage and sealing test',
  'steps.10.item6': 'Printing and finishing condition check',
  'steps.10.item7': 'Packaging condition inspection',
  'steps.10.item8': 'Approved sample vs finished product comparison',
  'steps.10.item9': 'Customer-required inspection standard application',
  'steps.10.item10': 'Inspection report provision when required',

  // Step 11
  'steps.11.title': 'Packaging & Shipment Preparation',
  'steps.11.desc':
    'Inspected products are safely packaged and prepared for shipment.',
  'steps.11.note':
    'Export packaging and shipping documents are prepared in advance to avoid delays.',
  'steps.11.item1': 'Per-product protective packaging',
  'steps.11.item2': 'Inner packaging and export carton packaging',
  'steps.11.item3': 'Quantity and label check',
  'steps.11.item4': 'Damage and contamination prevention',
  'steps.11.item5': 'Palletization check',
  'steps.11.item6': 'Packing list preparation',
  'steps.11.item7': 'Shipping document preparation',
  'steps.11.item8': 'Pre-shipment final confirmation',

  // Step 12
  'steps.12.title': 'International Shipping & Tracking',
  'steps.12.desc':
    'Your products are shipped to your specified country under the agreed delivery terms.',
  'steps.12.note':
    'Shipping periods and costs may vary by country, transport method, customs and local conditions.',
  'steps.12.item1': 'Express, air and sea freight',
  'steps.12.item2': 'Transport method and estimated schedule confirmation',
  'steps.12.item3': 'Carrier or forwarder connection',
  'steps.12.item4': 'Export customs document preparation',
  'steps.12.item5': 'Shipment progress updates',
  'steps.12.item6': 'Tracking information provision',
  'steps.12.item7': 'Destination arrival confirmation',

  // Step 13
  'steps.13.title': 'Delivery & After-Sales Support',
  'steps.13.desc':
    'After delivery, we finalize the order and provide ongoing after-sales support.',
  'steps.13.note':
    'When an issue arises, we analyze the cause using photos and records, and respond according to the agreed policy.',
  'steps.13.item1': 'Delivery completion confirmation',
  'steps.13.item2': 'Received product and quantity check',
  'steps.13.item3': 'Damage or quality issue reporting',
  'steps.13.item4': 'Relevant photo and record review',
  'steps.13.item5': 'Root cause analysis',
  'steps.13.item6': 'Response based on agreed policy',
  'steps.13.item7': 'Reorder and follow-up project support',

  'steps.1.short': 'Search & Inquiry',
  'steps.2.short': 'Spec Review',
  'steps.3.short': 'Quotation',
  'steps.4.short': 'Sample & Approval',
  'steps.5.short': 'Order & Payment',
  'steps.6.short': 'Pre-Production',
  'steps.7.short': 'Material QC',
  'steps.8.short': 'Mass Production',
  'steps.9.short': 'In-Process QC',
  'steps.10.short': 'Final Inspection',
  'steps.11.short': 'Packaging',
  'steps.12.short': 'Shipping & Tracking',
  'steps.13.short': 'Delivery & Support',

  // Stock vs Custom
  'compare.label': 'Two Paths',
  'compare.title': 'Stock Products vs Custom Production',
  'compare.subtitle':
    'Stock items and custom production follow different processes and timelines. Choose the path that fits your needs.',
  'compare.stock.title': 'Stock Products',
  'compare.stock.desc':
    'Ready-made items that can be shipped quickly after a simple confirmation.',
  'compare.stock.step1': 'Stock Check',
  'compare.stock.step2': 'Quotation',
  'compare.stock.step3': 'Payment',
  'compare.stock.step4': 'Release',
  'compare.stock.step5': 'Delivery',
  'compare.custom.title': 'Custom Production',
  'compare.custom.desc':
    'Made-to-order products built around your exact specification.',
  'compare.custom.step1': 'Spec Consultation',
  'compare.custom.step2': 'Quotation',
  'compare.custom.step3': 'Sample',
  'compare.custom.step4': 'Approval',
  'compare.custom.step5': 'Mass Production',
  'compare.custom.step6': 'Inspection',
  'compare.custom.step7': 'Delivery',

  // Order progress
  'progress.label': 'Order Progress Visibility',
  'progress.title': 'Track Your Order from My Page',
  'progress.subtitle':
    'Follow your order through every stage from inquiry to delivery, all from your personal dashboard.',
  'progress.status1': 'Inquiry Received',
  'progress.status2': 'Specification Review',
  'progress.status3': 'Quotation Sent',
  'progress.status4': 'Sample in Progress',
  'progress.status5': 'Sample Approval',
  'progress.status6': 'Order Confirmed',
  'progress.status7': 'In Production',
  'progress.status8': 'Quality Inspection',
  'progress.status9': 'Ready to Ship',
  'progress.status10': 'Shipped',
  'progress.status11': 'Delivered',
  'progress.note':
    'This is a visual example of how order status appears in your My Page. Live status updates are connected when order data is available.',

  // Quality assurance
  'qa.label': 'Continuous Quality',
  'qa.title': 'Quality Assurance at Every Stage',
  'qa.subtitle':
    'Quality is not checked only once. It is continuously managed across every following stage of production.',
  'qa.stage1.title': 'Incoming Material Inspection',
  'qa.stage1.desc': 'Raw materials and accessories are inspected before entering production.',
  'qa.stage2.title': 'Pre-Production Verification',
  'qa.stage2.desc': 'Production conditions are verified against the approved sample before starting.',
  'qa.stage3.title': 'In-Process Inspection',
  'qa.stage3.desc': 'Products are checked on the line to catch issues early.',
  'qa.stage4.title': 'Final Inspection',
  'qa.stage4.desc': 'Finished products receive a final check before shipment.',
  'qa.stage5.title': 'Packaging Inspection',
  'qa.stage5.desc': 'Packaging is verified for protection and correct labeling.',
  'qa.stage6.title': 'Shipment Verification',
  'qa.stage6.desc': 'Shipping documents and loading are confirmed before dispatch.',

  // Final CTA
  'cta.label': 'Ready to Begin',
  'cta.title': 'Start Your Packaging Project',
  'cta.subtitle':
    'Whether you need a stock container or a fully custom product, our team is ready to guide you through every step.',
  'cta.browse': 'Browse Products',
  'cta.quote': 'Request a Quote',
  'cta.custom': 'Start Your Custom Project',
  'cta.contact': 'Contact Our Team',
};